import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { RegularButton } from '../../shared/ui/regular-button/regular-button';
import { GenericInputField } from '../../shared/ui/generic-input-field/generic-input-field';
import { Dropdown, DropdownSection } from '../../shared/ui/dropdown/dropdown';
import { ResultsGrid } from '../results-grid/results-grid';
import { SvgIconComponent } from 'angular-svg-icon';
import { ResultsService } from '../../services/results.service';
import { LOCATIONS } from './locations';
import { SearchService } from '../../services/search.service';
import { UserService } from '../../services/user.service';

type AuctionMode = 'no' | 'yes' | 'only';

function greaterThanOrEqualToMin(minControlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent;
    if (!parent) return null;
    const minVal = parseFloat(parent.get(minControlName)?.value);
    const maxVal = parseFloat(control.value);
    if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
      return { lessThanMin: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-search',
  imports: [RegularButton, GenericInputField, Dropdown, ResultsGrid, SvgIconComponent],
  templateUrl: './search.html',
})
export class Search implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  public resultsService = inject(ResultsService);
  public searchService = inject(SearchService);
  public userService = inject(UserService);
  public hasSearched = signal(false);
  public headerTapCount = signal(0);
  public showClientId = computed(() => this.headerTapCount() >= 5);
  public clientIdCopied = signal(false);

  public searchForm = new FormGroup({
    priceMin: new FormControl('', [Validators.min(10000)]),
    priceMax: new FormControl('', [Validators.min(10000), greaterThanOrEqualToMin('priceMin')]),
    sizeMin: new FormControl('', [Validators.min(10)]),
    sizeMax: new FormControl('', [Validators.min(10), greaterThanOrEqualToMin('sizeMin')]),
    areaId: new FormControl('', [Validators.required]),
    auction: new FormControl<AuctionMode>('no', { nonNullable: true }),
  });

  public dropdownSections: DropdownSection[] = [{ options: [...LOCATIONS] }];
  public auctionSections: DropdownSection<AuctionMode>[] = [
    {
      options: [
        { id: 1, label: 'Όχι', value: 'no', type: 'default' },
        { id: 2, label: 'Ναι', value: 'yes', type: 'default' },
        { id: 3, label: 'Μόνο', value: 'only', type: 'default' },
      ],
    },
  ];

  ngOnInit(): void {
    this.searchForm
      .get('priceMin')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.searchForm.get('priceMax')?.updateValueAndValidity({ emitEvent: false });
      });

    this.searchForm
      .get('sizeMin')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.searchForm.get('sizeMax')?.updateValueAndValidity({ emitEvent: false });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public searchInProgress = effect(() => {
    const inProgress = this.searchService.isTyping() || this.searchService.isLoadingMore();
    if (inProgress) {
      this.searchForm.controls.areaId.disable();
      this.searchForm.controls.priceMax.disable();
      this.searchForm.controls.priceMin.disable();
      this.searchForm.controls.sizeMax.disable();
      this.searchForm.controls.sizeMin.disable();
      this.searchForm.controls.auction.disable();
    } else {
      this.searchForm.controls.areaId.enable();
      this.searchForm.controls.priceMax.enable();
      this.searchForm.controls.priceMin.enable();
      this.searchForm.controls.sizeMax.enable();
      this.searchForm.controls.sizeMin.enable();
      this.searchForm.controls.auction.enable();
    }
  });

  get priceMinError(): string {
    if (this.searchForm.get('priceMin')?.hasError('min')) {
      return 'Η τιμή πρέπει να είναι τουλάχιστον 10.000€';
    }
    return '';
  }

  get priceMaxError(): string {
    const ctrl = this.searchForm.get('priceMax')!;
    if (ctrl.hasError('lessThanMin'))
      return 'Η μέγιστη τιμή πρέπει να είναι μεγαλύτερη από την ελάχιστη';
    if (ctrl.hasError('min')) return 'Η τιμή πρέπει να είναι τουλάχιστον 10.000€';
    return '';
  }

  get sizeMinError(): string {
    if (this.searchForm.get('sizeMin')?.hasError('min')) {
      return 'Το εμβαδόν πρέπει να είναι τουλάχιστον 10τμ';
    }
    return '';
  }

  get sizeMaxError(): string {
    const ctrl = this.searchForm.get('sizeMax')!;
    if (ctrl.hasError('lessThanMin'))
      return 'Το μέγιστο εμβαδόν πρέπει να είναι μεγαλύτερο από το ελάχιστο';
    if (ctrl.hasError('min')) return 'Το εμβαδόν πρέπει να είναι τουλάχιστον 10τμ';
    return '';
  }

  get areaInvalid(): boolean {
    const ctrl = this.searchForm.get('areaId')!;
    return ctrl.invalid && ctrl.touched;
  }

  copyClientId(): void {
    navigator.clipboard.writeText(this.userService.clientId());
    this.clientIdCopied.set(true);
    setTimeout(() => this.clientIdCopied.set(false), 1500);
  }

  onClick(): void {
    if (this.searchService.isLoadingMore() || this.searchService.isTyping()) {
      this.searchService.stopSearch();
    } else {
      this.searchForm.markAllAsTouched();
      if (this.searchForm.valid) {
        this.resultsService.clearResults();
        this.hasSearched.set(true);
        this.searchService.search({
          listing_type: 'Αγορά',
          price_min: this.searchForm.value.priceMin
            ? parseInt(this.searchForm.value.priceMin as string)
            : undefined,
          price_max: this.searchForm.value.priceMax
            ? parseInt(this.searchForm.value.priceMax as string)
            : undefined,
          size_min: this.searchForm.value.sizeMin
            ? parseInt(this.searchForm.value.sizeMin as string)
            : undefined,
          size_max: this.searchForm.value.sizeMax
            ? parseInt(this.searchForm.value.sizeMax as string)
            : undefined,
          area_id: this.searchForm.value.areaId?.toString() ?? '',
          auction: this.searchForm.controls.auction.value,
        });
      }
    }
  }
}
